import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import type { ForwardedRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Extension } from '@tiptap/core';
import { COMMENT_TOOLS, isSafeLinkHref, normalizeLinkHref } from './commentKeymap';
import { CommentMention } from './commentMentionNode';
import { commentMentionAttrs, searchCommentMentions } from './commentMentions';
import type { CommentMentionRow, MentionPerson } from './commentMentions';
import { LINK_OPTIONS, NonInclusiveLink } from '../notes/linkOptions';
import { buildSuggestion } from '../notes/mentionSuggestion';
import { RecordLinkSuggestion, searchRecordLinks } from './recordSuggestion';
import { CommandMenuSuggestion } from './commandMenu';
import type { CommandMenuPerson } from './commandMenu';
import { EmojiButton, GifButton } from './CommentInsertPickers';
import { giphyApiKey } from './giphy';
import type { MentionSources } from '../notes/noteMentions';
import type { SupabaseClient } from '../../lib/supabase';
import { PasteUploadPreview, usePasteUploadPreview } from '../../components/PasteUploadPreview';

/**
 * The comment box.
 *
 * Deliberately a different editor from the one under /notes. A note is a
 * document and wants headings, fonts and spacing; a comment is a message and
 * wants none of them. Sharing one component would mean a comment box with an
 * H1 button in it, which is an invitation to use the wrong tool.
 *
 * What IS shared is the part worth sharing: the `@` popover, its key handling
 * and its out-of-order guard all come from notes/mentionSuggestion. What
 * differs is what the popover searches — here it searches PEOPLE as well as
 * records, because a mention in a thread is usually somebody being asked a
 * question, and that mention has to become a notification.
 */

export interface CommentEditorProps {
  value: string;
  onChange: (html: string) => void;
  onSubmit: () => void;
  client: SupabaseClient;
  sources: MentionSources;
  /** Active TAP people, already loaded for the thread. */
  people: MentionPerson[];
  /** Active TAP people eligible for command actions; defaults to `people`. */
  commandPeople?: CommandMenuPerson[];
  /** Messages page navigation for command actions. */
  commandNavigate?: (href: string) => void | Promise<void>;
  label: string;
  disabled?: boolean;
  /** Upload a pasted screenshot and return its src. */
  onPasteImage?: (file: File) => Promise<string>;
  onPasteError?: (cause: unknown) => void;
  /** True while a pasted screenshot uploads, so the caller can hold Send and Save. */
  onUploadingChange?: (uploading: boolean) => void;
  /**
   * A `#` picker for deals, leads and orders, on top of the existing `@`.
   * Off everywhere except the Messages page (a boolean prop rather than a
   * config object) so every other CommentEditor call site is byte-for-byte
   * unaffected (plan-drop3-extras.md H4).
   */
  enableRecordPicker?: boolean;
}

/** Imperative handle for callers that need to insert content programmatically
 * — the Messages page's paperclip button, which is not a paste event and so
 * cannot go through onPasteImage/handlePaste. */
export interface CommentEditorHandle {
  /** Inserts a link with the given text at the caret, then a trailing space. */
  insertLink: (label: string, href: string) => void;
}

/**
 * Enter sends, Shift+Enter breaks the line.
 *
 * A ProseMirror keymap rather than a React onKeyDown, because the editor
 * handles Enter itself and a React handler runs after it has already inserted
 * a paragraph. The rules live in commentKeymap.ts; this only wires them.
 */
function submitKeymap(onSubmit: () => void, isUploading: () => boolean) {
  return Extension.create({
    name: 'commentSubmit',
    addKeyboardShortcuts() {
      return {
        Enter: ({ editor }) => {
          // The mention popover registers its own Enter handler at a higher
          // priority and returns true while it is open, so by the time this
          // runs the popover has already had its say.
          if (editor.isActive('bulletList') || editor.isActive('orderedList')) {
            return false;
          }
          if (editor.isActive('codeBlock')) {
            return false;
          }
          if (editor.isEmpty) {
            // Swallowed rather than passed through: a blank comment posted by
            // a stray Enter is a row nobody can tidy up.
            return true;
          }
          if (isUploading()) {
            // A screenshot is still on its way to Drive. Sending now would
            // post the comment without it (design note S16), so Enter waits.
            return true;
          }
          onSubmit();
          return true;
        },
        // Explicit, so it keeps working inside lists and code blocks where
        // the default Enter has been taken over above.
        'Shift-Enter': ({ editor }) => editor.commands.setHardBreak(),
      };
    },
  });
}

function CommentEditorInner(
  {
    value,
    onChange,
    onSubmit,
    client,
    sources,
    people,
    commandPeople,
    commandNavigate,
    label,
    disabled = false,
    onPasteImage,
    onPasteError,
    onUploadingChange,
    enableRecordPicker = false,
  }: CommentEditorProps,
  ref: ForwardedRef<CommentEditorHandle>,
) {
  // The submit handler changes identity every render; the keymap must not be
  // rebuilt for that, so it reads through a ref.
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;

  // Same for the people list and the paste handler: the editor is built once
  // (the dependency array below is empty on purpose — rebuilding it would
  // throw away the person's half-typed comment), so anything that changes
  // between renders has to be reachable through a ref rather than captured.
  const peopleRef = useRef(people);
  peopleRef.current = people;
  const commandPeopleRef = useRef(commandPeople ?? people);
  commandPeopleRef.current = commandPeople ?? people;
  const commandNavigateRef = useRef(commandNavigate);
  commandNavigateRef.current = commandNavigate;
  const pasteRef = useRef(onPasteImage);
  pasteRef.current = onPasteImage;
  const pasteErrorRef = useRef(onPasteError);
  pasteErrorRef.current = onPasteError;
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;
  const upload = usePasteUploadPreview(onUploadingChange);
  const uploadRef = useRef(upload);
  uploadRef.current = upload;

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          // A comment is a message. Headings and horizontal rules in a thread
          // are somebody reaching for the wrong tool.
          heading: false,
          horizontalRule: false,
          // Off here, replaced below — same reason as the notes editor: the
          // mark boundary can only be closed by extending the mark.
          link: false,
        }),
        NonInclusiveLink.configure({
          ...LINK_OPTIONS,
          // Belt and braces with the render-side sanitizer: this stops an
          // unsafe scheme entering the document in the first place.
          isAllowedUri: (url: string) => isSafeLinkHref(url),
        }),
        // Pasted screenshots. Not draggable and not resizable: a comment is a
        // message, and an image somebody can drag around inside one is a
        // layout tool nobody asked for.
        Image.configure({ inline: false, allowBase64: false }),
        CommentMention.configure({
          HTMLAttributes: {},
          suggestion: buildSuggestion<CommentMentionRow>({
            search: (query) => searchCommentMentions(client, query, sources, peopleRef.current),
            attrs: commentMentionAttrs,
            ariaLabel: 'Mention a person, project, task or order',
          }) as never,
        }),
        // Messages page only (enableRecordPicker), read once at mount: the
        // page that turns this on never flips it off mid-session, so a ref
        // for sources (not for the flag itself) is enough to keep the
        // picker's permission gates current as the caller's own queries
        // resolve.
        ...(enableRecordPicker
          ? [
              RecordLinkSuggestion({
                search: (query) => searchRecordLinks(client, query, sourcesRef.current),
              }),
              CommandMenuSuggestion({
                client,
                sources: () => sourcesRef.current,
                people: () => commandPeopleRef.current,
                navigate: () => commandNavigateRef.current,
              }),
            ]
          : []),
        submitKeymap(
          () => submitRef.current(),
          () => uploadRef.current.isUploading(),
        ),
      ],
      content: value,
      editable: !disabled,
      editorProps: {
        attributes: { class: 'comment-editor-surface', 'aria-label': label },
        handlePaste: (_view, event) => {
          const upload = pasteRef.current;
          if (upload === undefined) {
            return false;
          }
          const file = imageFromClipboard(event.clipboardData);
          if (file === null) {
            return false;
          }
          // Handled, so the browser does not also drop a base64 copy of the
          // same screenshot into the document — which would sail past the
          // image-host allowlist by not having a host at all.
          event.preventDefault();
          // Shown at once beside the editor while it uploads; only the Drive
          // link goes into the document (PasteUploadPreview.tsx).
          void uploadRef.current
            .track(file, () => upload(file))
            .then(
              (src) => {
                editorRef.current?.chain().focus().setImage({ src }).run();
              },
              (cause: unknown) => pasteErrorRef.current?.(cause),
            );
          return true;
        },
      },
      onUpdate: ({ editor: instance }) => {
        onChange(instance.getHTML());
      },
    },
    [],
  );

  // handlePaste is built once and cannot close over `editor`, which does not
  // exist yet at that point.
  const editorRef = useRef<typeof editor>(null);
  editorRef.current = editor;

  useImperativeHandle(
    ref,
    () => ({
      insertLink: (label: string, href: string) => {
        editorRef.current
          ?.chain()
          .focus()
          .insertContent([
            { type: 'text', marks: [{ type: 'link', attrs: { href } }], text: label },
            { type: 'text', text: ' ' },
          ])
          .run();
      },
    }),
    [],
  );

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Clearing after a post: the page sets value back to '' and the editor has
  // to follow, but ONLY when it is genuinely a reset — comparing on every
  // render would fight the person typing.
  useEffect(() => {
    if (editor !== null && value === '' && !editor.isEmpty) {
      editor.commands.clearContent();
    }
  }, [editor, value]);

  if (editor === null) {
    return <div className="comment-editor-loading">Loading…</div>;
  }

  // Read per render, not at module load, so a test can stub the variable.
  // No key in the build: no GIF button at all (Drop 3b missing-key guard).
  const gifKey = giphyApiKey();

  return (
    <div className="comment-editor">
      <div className="comment-editor-toolbar" role="toolbar" aria-label="Comment formatting">
        {COMMENT_TOOLS.map((tool) => (
          <button
            key={tool.id}
            type="button"
            className={`comment-tool${editor.isActive(tool.id) ? ' comment-tool--on' : ''}`}
            aria-label={tool.label}
            aria-pressed={tool.id === 'link' ? undefined : editor.isActive(tool.id)}
            disabled={disabled}
            onClick={() => {
              if (tool.id === 'link') {
                const typed = window.prompt('Link address');
                if (typed === null) return;
                const href = normalizeLinkHref(typed);
                if (!isSafeLinkHref(href)) {
                  editor.chain().focus().unsetLink().run();
                  return;
                }
                editor.chain().focus().setLink({ href }).run();
                return;
              }
              if (tool.id === 'bulletList') {
                editor.chain().focus().toggleBulletList().run();
                return;
              }
              editor.chain().focus().toggleMark(tool.id).run();
            }}
          >
            {tool.glyph}
          </button>
        ))}
        {/* Drop 3b: the same two buttons wherever this editor is used
            (Messages, task and project comments, deals, Social Studio). */}
        <EmojiButton
          disabled={disabled}
          onPick={(emoji) => {
            // A text node, not a string: insertContent parses a string as
            // HTML, and an emoji is only ever text.
            editor.chain().focus().insertContent({ type: 'text', text: emoji }).run();
          }}
        />
        {gifKey === null ? null : (
          <GifButton
            apiKey={gifKey}
            disabled={disabled}
            onPick={(gif) => {
              // gif.url has already passed cleanGiphyUrl (https, a GIPHY
              // media host, no query); the renderer checks it again.
              editor.chain().focus().setImage({ src: gif.url, alt: gif.title }).run();
            }}
          />
        )}
        <span className="comment-editor-hint">Enter to send · Shift+Enter for a new line</span>
      </div>
      <EditorContent editor={editor} />
      <PasteUploadPreview src={upload.preview} />
    </div>
  );
}

/** The first image on the clipboard, if this paste carries one. */
export function imageFromClipboard(data: DataTransfer | null): File | null {
  if (data === null) {
    return null;
  }
  for (const item of Array.from(data.files)) {
    if (item.type.startsWith('image/')) {
      return item;
    }
  }
  return null;
}

/**
 * The forwardRef wrapper. A named function component (CommentEditorInner)
 * wrapped once, so every existing call site that destructures props as
 * before keeps typechecking — forwardRef changes only how a ref is attached,
 * never what the props look like.
 */
export const CommentEditor = forwardRef<CommentEditorHandle, CommentEditorProps>(
  CommentEditorInner,
);
